import {Action, WorkflowNodeSetting, WorkflowNodeTypes} from "@/types/node";
import {Edge, Node} from "@xyflow/react";
import {getSettingValueByKey} from "@/utils/index";
//TODO: MOVE THIS LOGIC TO NODE LEVEL
export class PromptBuilder {
  private nodes: Node[];
  private edges: Edge[];
  private globalNode: Node | undefined;
  private globalNodeSettings: WorkflowNodeSetting[];
  private startNode: Node | undefined;
  private startNodeSettings: WorkflowNodeSetting[];
  private conversationNodes: Node[];
  private faqList: Record<string, string>[];
  private specialCasesList: Record<string, string>[];
  private globalInstructions: string;
  private globalActions: Action[];

  private sortNodesPreorder(nodes: Node[], edges: Edge[]): Node[] {
    const visited = new Set();
    const sorted: Node[] = [];

    const preorderTraversal = (nodeId: string) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const currentNode = nodes.find(node => node.id === nodeId);
      if (currentNode) sorted.push(currentNode);

      const outgoingEdges = edges.filter(edge => edge.source === nodeId);
      outgoingEdges.forEach(edge => preorderTraversal(edge.target));
    };

    const rootEdge = edges.find(edge => edge.source === "root");
    if (rootEdge) preorderTraversal(rootEdge.target);

    return sorted;
  }

  constructor(nodes: Node[], edges: Edge[]) {
    if (nodes.length < 2) return;

    this.edges = edges;
    // Filter out placeholder nodes and sort the nodes in preorder traversal
    this.nodes = this.sortNodesPreorder(nodes, edges);
    const shouldStartWithStartNode =
      this.nodes[1].data.type === WorkflowNodeTypes.BRANCH ||
      this.nodes[1].data.isEmpty;
    this.conversationNodes = this.sortNodesPreorder(
      this.nodes.filter(
        node =>
          !node.data.isEmpty &&
          (node.data.type === WorkflowNodeTypes.STEP ||
            (shouldStartWithStartNode &&
              node.data.type === WorkflowNodeTypes.START)),
      ),
      edges,
    );

    this.startNode = this.nodes.find(
      node => node.data.type === WorkflowNodeTypes.START,
    );
    this.globalNode = this.nodes.find(
      node => node.data.type === WorkflowNodeTypes.GLOBAL,
    );
    this.globalNodeSettings =
      (this.globalNode?.data?.settings as WorkflowNodeSetting[]) || [];

    this.faqList =
      (getSettingValueByKey(this.globalNodeSettings, "faq") as Record<
        string,
        string
      >[]) || [];
    this.specialCasesList =
      (getSettingValueByKey(this.globalNodeSettings, "special_cases") as Record<
        string,
        string
      >[]) || [];
    this.globalInstructions =
      (getSettingValueByKey(
        this.globalNodeSettings,
        "global_instructions",
      ) as string) || "";
    this.globalActions =
      (getSettingValueByKey(
        this.globalNodeSettings,
        "global_actions",
      ) as Action[]) || [];
  }

  public buildMarkdown(): string {
    return [
      this.buildIntroductionSection(),
      this.buildFAQSection(),
      this.buildSpecialCasesAndObjectionsSection(),
      this.buildGreetingSection(),
      this.buildConversationStepsSection(),
      this.buildFooterSection(),
    ]
      .map(section => section.replace(/\u200B/g, ""))
      .join("\n\n");
  }

  private buildIntroductionSection(): string {
    const isBackgroundInfomation =
      this.faqList.length > 0 ||
      this.specialCasesList.length > 0 ||
      this.globalActions.length > 0;
    return [
      MD.header(1, "Assistant Guidelines"),
      MD.header(2, "General Behaviour"),
      MD.header(3, "Style Guide"),
      `- **Be human-like:** Engage in a natural vocal conversation with the user. Speak naturally and respond based on your instructions and the provided transcript.
- **Be concise:** Keep responses short and to the point. Tackle one question or action item at a time without combining multiple thoughts into one response.
- **Do not repeat:** Avoid directly repeating what's in the transcript. Instead, rephrase as necessary and vary sentence structures and vocabulary for unique, personalized responses.
- **Be conversational:** Talk as though you're speaking to a close friend—use everyday language. Occasionally, use filler words while keeping the prose short. Avoid overly formal language.
- **Reply with emotions:** Use human-like emotions and attitudes to create engaging and personalized responses. Incorporate humor, wit, empathy, and surprise as appropriate. Don't be a pushover.
- **Be proactive:** Lead the conversation and engage users by suggesting a next step or asking a question.
- **Overcome ASR errors:** Be prepared for real-time transcription errors. Guess user intentions when possible and respond. Use colloquial phrases like "didn't catch that" or "you're coming through choppy" for clarification. Confirm you understood the user correctly without mentioning transcription errors. For emails and addresses, ask for spelling confirmation (i.e., example at domain dot com).
- **Always stick to your role:** Stay within the bounds of your role's capabilities. If you can't do something, steer the conversation to your role's goals creatively and lively.
- **Create smooth conversation:** Ensure responses fit your role and maintain a human-like flow in live calling sessions, responding directly to the user input.
- **Mind your diction:** Ensure output is suitable for speech synthesis. Write numbers and phone numbers in full letters and pronounce percentages with each digit followed by "percent" (i.e., 1.99% -> "one point nine nine percent").
      `,
      isBackgroundInfomation ? MD.header(2, "Background Information") : "",
    ].join("\n");
  }

  private buildFAQSection(): string {
    if (this.faqList.length === 0) return "";

    const faqItems = this.faqList
      .map((faq: {title: string; description: string}) => {
        return [
          MD.header(4, `Question: ${faq.title}`),
          MD.text(`Answer:\n${faq.description}`),
        ].join("\n");
      })
      .join("\n\n");

    return [
      MD.header(3, "Frequently Asked Questions"),
      MD.text(
        "If you answer a question in the middle of a conversation stage, make sure to steer the conversation back to where you left off in the stage. Make sure you answer in context of the current conversation.",
      ),
      faqItems,
    ].join("\n");
  }

  private buildSpecialCasesAndObjectionsSection(): string {
    if (this.specialCasesList.length === 0) return "";

    const specialCasesItems = this.specialCasesList
      .map((specialCase: {title: string; description: string}) => {
        return [
          MD.header(4, `Case: ${specialCase.title}`),
          MD.text(`${specialCase.description}`),
        ].join("\n");
      })
      .join("\n");

    return [
      MD.header(3, "Special Cases and Objections"),
      specialCasesItems,
    ].join("\n");
  }

  private buildGreetingSection(): string {
    if (!this.startNode) return "";

    const greeting =
      (getSettingValueByKey(
        this.startNode.data.settings as WorkflowNodeSetting[],
        "scripted_greeting",
      ) as string) || "";
    return [MD.header(3, "Greeting"), MD.text(greeting)].join("\n");
  }

  private buildJumpNodeSection(node: Node): string {
    if (!node) return "";

    const jumpToNodeId = getSettingValueByKey(
      node.data.settings as WorkflowNodeSetting[],
      "jump_to_step",
    );
    const jumpToNode = this.nodes.find(n => n.id === jumpToNodeId);
    const jumpToNodeName = jumpToNode
      ? getSettingValueByKey(
          jumpToNode.data.settings as WorkflowNodeSetting[],
          "name",
        )
      : "N/A";
    const jumpToNodeIndex = this.conversationNodes.findIndex(
      n => n.id === jumpToNodeId,
    );
    const jumpToNodeType = jumpToNode
      ? jumpToNode.data.type.toString().charAt(0).toUpperCase() +
        jumpToNode.data.type.toString().slice(1)
      : "N/A";

    return `[${jumpToNodeType}${!jumpToNodeIndex ? ` ${jumpToNodeIndex + 1}` : ""}: ${jumpToNodeName}]`;
  }

  private buildWhereToNextSection(node: Node, index = 0): string {
    if (!node) return "";
    let whereToNext = "";

    const nextNodeIds =
      this.edges
        .filter(edge => edge.source === node.id)
        .map(edge => edge.target) || [];
    const nextNodes =
      this.nodes.filter(node => nextNodeIds.includes(node.id)) || [];

    if (nextNodes.length === 0) return "";
    if (nextNodes[0].type === WorkflowNodeTypes.BRANCH) {
      whereToNext = nextNodes
        .map(node => {
          const name = getSettingValueByKey(
            node.data.settings as WorkflowNodeSetting[],
            "name",
          );
          const description = getSettingValueByKey(
            node.data.settings as WorkflowNodeSetting[],
            "description",
          );

          let targetNodeID = this.edges.find(
            edge => edge.source === node.id,
          )?.target;
          let targetNode = this.nodes.find(n => n.id === targetNodeID);

          // If target node is empty, find the next non-empty node in preorder traversal
          if (!targetNode || targetNode.data.isEmpty) {
            let nodeIndex = this.nodes.findIndex(n => n.id === node.id);
            while (nodeIndex + 1 < this.nodes.length) {
              nodeIndex++;
              targetNode = this.nodes[nodeIndex];
              if (targetNode && !targetNode.data.isEmpty) {
                targetNodeID = targetNode.id;
                break;
              }
            }
          }
          let continueWithText = "";
          const targetNodeType = targetNode?.data.type;

          if (targetNodeType === WorkflowNodeTypes.JUMP) {
            continueWithText = `-> Then continue with ${this.buildJumpNodeSection(targetNode)}`;
          } else {
            const targetNodeIndex = targetNode
              ? this.conversationNodes.findIndex(n => n.id === targetNodeID)
              : -1;
            const targetNodeName = targetNode?.data.isEmpty
              ? getSettingValueByKey(
                  targetNode.data.settings as WorkflowNodeSetting[],
                  "name",
                ) || ""
              : "";

            const nextNodeAddress = `[Step ${targetNodeIndex + 1}${targetNodeName ? `: ${targetNodeName}` : ""}]`;
            continueWithText =
              targetNodeIndex > 0
                ? `-> then continue with ${nextNodeAddress}`
                : "then the branch ends here";
          }

          const isBranchEmpty = !!node?.data.isEmpty;

          return !isBranchEmpty
            ? `- if: [${name}] ${description ? `- [${description.toString().replace(/\n/g, "; ")}] ` : ""}${continueWithText}`
            : "";
        })
        .join("\n");
    } else if (nextNodes[0].type === WorkflowNodeTypes.JUMP) {
      whereToNext = this.buildJumpNodeSection(nextNodes[0]);
    } else if (nextNodes[0].type === WorkflowNodeTypes.PLACEHOLDER) {
      whereToNext = `then the branch ends here`;
    }

    return whereToNext;
  }

  private buildStepActionsSection(node: Node): string {
    if (!node) return "";

    const stepActions = getSettingValueByKey(
      node.data.settings as WorkflowNodeSetting[],
      "actions",
    ) as Action[];

    if (!stepActions || stepActions?.length === 0) return "";

    return [
      MD.header(4, `Available Step Actions`),
      stepActions
        ? stepActions
            .map((action: Action) => {
              return [
                MD.header(5, action.name),
                MD.text(
                  action.parameters_hard_coded.prompt ||
                    action.parameters_hard_coded.description ||
                    "",
                ),
              ].join("\n");
            })
            .join("\n")
        : "",
    ].join("\n");
  }

  private buildNodeSection(node: Node, index = 0): string {
    if (!node) return "";

    const stepTitle =
      getSettingValueByKey(
        node.data.settings as WorkflowNodeSetting[],
        "name",
      ) || "";
    const stepInstructions =
      getSettingValueByKey(
        node.data.settings as WorkflowNodeSetting[],
        "instructions",
      ) ||
      getSettingValueByKey(
        node.data.settings as WorkflowNodeSetting[],
        "description",
      ) ||
      "";

    const whereToNext = this.buildWhereToNextSection(node, index);
    const nodeType =
      node.data.type.toString().charAt(0).toUpperCase() +
      node.data.type.toString().slice(1);
    const nodePromptHeading =
      node.data.type === WorkflowNodeTypes.START
        ? `Step ${index + 1}: Start`
        : `${nodeType} ${index + 1}: ${stepTitle}`;

    return [
      MD.header(3, nodePromptHeading),
      stepInstructions,

      // ACTIONS
      this.buildStepActionsSection(node),

      // WHERE TO NEXT
      ...(whereToNext
        ? [MD.header(4, "Where to go next?"), MD.text(`${whereToNext}`)]
        : []),
    ].join("\n\n");
  }

  private buildConversationStepsSection(): string {
    if (this.conversationNodes.length === 0) return "";

    const nodes = this.conversationNodes
      .map((node, index) => this.buildNodeSection(node, index))
      .join("\n\n");

    return [MD.header(2, "Conversation Steps"), nodes].join("\n");
  }

  private buildFooterSection(): string {
    if (this.globalInstructions === "" && this.globalActions.length === 0)
      return "";

    const globalInstructions = this.globalInstructions
      ? [
          MD.header(2, "General Instructions"),
          MD.text(this.globalInstructions),
        ].join("\n\n")
      : "";

    const globalActions = this.globalActions
      ? [
          MD.header(3, "Available General Actions"),
          this.globalActions
            .map((action: Action) => {
              return [
                MD.header(4, action.name),
                MD.text(
                  action.parameters_hard_coded.prompt ||
                    action.parameters_hard_coded.description ||
                    "",
                ),
              ].join("\n");
            })
            .join("\n"),
        ].join("\n\n")
      : "";

    return [globalInstructions, globalActions].join("\n");
  }
}

class MD {
  static header(level: number, text: string): string {
    return `${"#".repeat(level)} ${text}\n`;
  }
  static text(text: string): string {
    return `${text}\n`;
  }

  static table(headers: string[], rows: string[][]): string {
    const headerRow = `| ${headers.join(" | ")} |`;
    const dividerRow = `| ${headers.map(() => "---").join(" | ")} |`;
    const bodyRows = rows.map(row => `| ${row.join(" | ")} |`).join("\n");
    return `${headerRow}\n${dividerRow}\n${bodyRows}`;
  }
}
